import React, { useEffect, useRef, Fragment } from "react";
import { useFormikContext } from "formik";
import { prepareManifestForEditor } from "../common";
import get from "lodash/get";
import PageFactory from "components/FormerEditor/PageFactory";
import Padder from "components/Padder";
import EditorToolbar from "components/EditorToolbar";
import PreviewLoader from "components/FormerEditor/EditorLoader/PreviewLoader";
import deepEqual from "deep-equal";
import { STATE_KEYS } from "utils/constants/state";
import {
  useEditorViewport,
  useModelQueryId,
  usePrevious,
  withWebfont
} from "utils/hooks";
import {
  PageFactoryContextProvider,
  usePageFactoryContext
} from "utils/context";
import StripeConnectBanner from "components/StripeConnectBanner";
import PagePreviewBrowserBezel from "components/PagePreviewBrowserBezel";
import {
  FORMER_MAX_WIDTH,
  VIEWPORT_MOBILE_WIDTH,
  VIEWPORT_MOBILE_THRESHOLD,
  UI_THEME,
  EDITOR_PREVIEW_ID
} from "utils/constants/ui";
import { getBrandTheme } from "utils/theme";
import { APPLICATION, PAGE } from "utils/constants/models";
import { devLogger } from "utils/debug";
import {
  useAppAdmin,
  useAppEntities,
  useFeatureGroupFeatures,
  useProductsPrices,
  useManifestShortLinks,
  useTaxRates,
  useCouponPromotionCodes,
  useShippingRates,
  useCommonEntitlementConfigData,
  useCtxReferences,
  useIsAuthed
} from "utils/selectors";
import { getBlocksStyles } from "constants/styles/blocks";
import { VIEWPORT_SIZE } from "utils/constants/viewport";
import { useResizeDetector } from "react-resize-detector";
import { useEntitlement } from "@priceblocs/react-priceblocs-js";
import { PRODUCT_FEATURE_CAPABILITY } from "utils/constants/capability";
import EntitlementsUpgradeContainer from "components/EntitlementsUpgradeContainer";
import { isPlatformSubdomain } from "utils/subdomain";
import { useEditorManifestLiveUrl } from "utils/hooks/manifest";
import { useAdminContext } from "utils/state";
import { getAdminShowBanner } from "utils/mapStateToProps";
import { getPreviewStyles } from "constants/styles/preview";
import ValidationsBar from "components/ValidationsBar";

/**
 * canActivateSettings - alias for "isBuilder". Also will prevent event tracking
 * stateKey - used for path get prefix
 */
const PREVIEW_PAGE_FACTORY_SETTINGS = {
  canActivateSettings: true,
  stateKey: STATE_KEYS.MANIFEST.DATA
};

/**
 * Observe configuration and content values
 * Recompute on change
 */
const useRecomputeManifest = () => {
  const { values, setFieldValue } = useFormikContext();
  const applicationUUID = useModelQueryId(APPLICATION);
  const manifestUUID = useModelQueryId(PAGE);
  const prevContent = usePrevious(get(values, STATE_KEYS.MANIFEST.CONTENT));
  const changedContent = !deepEqual(
    get(values, STATE_KEYS.MANIFEST.CONTENT),
    prevContent
  );
  const didMountRef = useRef(false);
  const { products, prices } = useProductsPrices();
  const { featureGroups, features } = useFeatureGroupFeatures();
  const taxRates = useTaxRates();
  const { coupons } = useCouponPromotionCodes();
  const shippingRates = useShippingRates();
  const entitlements = useCommonEntitlementConfigData();

  const manifestShortLinks = useManifestShortLinks();
  const appEntities = useAppEntities();
  const admin = useAppAdmin(applicationUUID, manifestUUID);

  const recomputeManifest = () => {
    devLogger("recomputeManifest");
    if (values.manifest) {
      const manifest = prepareManifestForEditor({
        manifest: values.manifest,
        admin,
        products,
        prices,
        features,
        shortLinks: manifestShortLinks,
        taxRates,
        coupons,
        shippingRates,
        entitlements,
        featureGroups,
        appEntities,
        variables: values.variables,
        theme: values.manifest.data.theme
      });
      setFieldValue("manifest", manifest);
    }
  };

  useEffect(recomputeManifest, [
    get(values, STATE_KEYS.MANIFEST.UPDATED_AT),
    get(values, STATE_KEYS.MANIFEST.CONFIG),
    get(values, STATE_KEYS.MANIFEST.PRICING_LAYOUT),
    get(values, STATE_KEYS.MANIFEST.PRICING_DEFAULT_INTERVAL),
    get(values, STATE_KEYS.MANIFEST.PRICING_DEFAULT_CURRENCY),
    get(values, STATE_KEYS.MANIFEST.FEATURES_CLUSIVITY)
  ]);

  useEffect(() => {
    if (didMountRef.current) {
      recomputeManifest();
    } else {
      didMountRef.current = true;
    }
  }, [changedContent]);
};

/**
 * Set the right context before building the page
 * - Important to toggle off the admin / builder capabilities which allow the builder to set menus and hover states active
 * - (see context util for more documentation )
 */
const PageFactoryInContext = () => {
  const isAuthed = useIsAuthed();
  const { values, setFieldValue, errors } = useFormikContext();
  const { width, ref } = useResizeDetector();
  const ctxWidth = !Number.isNaN(width) ? width : 0;

  const mobileThresholdReached = ctxWidth <= VIEWPORT_MOBILE_THRESHOLD;
  const { application } = useCtxReferences();
  const isPlatform = isPlatformSubdomain(application && application.subdomain);

  const liveManifestUrl = useEditorManifestLiveUrl();

  /**
   * Show banner check
   * - Admin connect banner status
   * - Entitlement upgrade
   * -- Note: don't show upgrade notice to the platform account
   */
  const admin = useAdminContext();
  const showBanner = getAdminShowBanner(admin);
  const transactionFeeEntitlement = useEntitlement(
    PRODUCT_FEATURE_CAPABILITY.TRANSACTION_FEE
  );
  const upgradePrice = get(transactionFeeEntitlement, "upgrade.price");
  const showUpgrade = Boolean(upgradePrice) && !isPlatform;
  const canShowBanner = isAuthed && (showBanner || showUpgrade);

  const {
    setActivateSettings,
    setStateKey,
    canActivateSettings
  } = usePageFactoryContext();
  const editorViewport = useEditorViewport();
  useEffect(() => {
    setActivateSettings(true);
    setStateKey(STATE_KEYS.MANIFEST.DATA);
  }, []);

  /**
   * In editor mode we observe the canvas width
   * and set the viewport to mobile when the threshold is passed
   */
  useEffect(() => {
    if (mobileThresholdReached && editorViewport !== VIEWPORT_SIZE.MOBILE) {
      setFieldValue(STATE_KEYS.EDITOR.VIEWPORT, VIEWPORT_SIZE.MOBILE);
    } else if (
      !mobileThresholdReached &&
      editorViewport === VIEWPORT_SIZE.MOBILE
    ) {
      setFieldValue(STATE_KEYS.EDITOR.VIEWPORT, VIEWPORT_SIZE.DESKTOP);
    }
  }, [mobileThresholdReached, canActivateSettings]);

  const theme = get(values, STATE_KEYS.THEME.DATA);
  const font = get(values, STATE_KEYS.THEME.FONT);
  const fontFamily = get(values, STATE_KEYS.THEME.FONT_FAMILY);
  const brandTheme = getBrandTheme(theme);
  withWebfont({ font });

  const { styles: blocksStyles } = getBlocksStyles(brandTheme);
  const { styles: previewStyles } = getPreviewStyles({
    fontFamily,
    editorViewport
  });

  let banner;
  if (canShowBanner) {
    banner = (
      <div className="w-100 center bb hairline-1 bg-white ph3">
        {showBanner ? (
          <StripeConnectBanner
            {...admin.account}
            description="Note: you can still build and publish pages before your account is fully enabled."
          />
        ) : (
          <EntitlementsUpgradeContainer
            component={UI_THEME.SLIM}
            features={[PRODUCT_FEATURE_CAPABILITY.TRANSACTION_FEE]}
          />
        )}
      </div>
    );
  }
  const hasValidationMessages = errors && errors.messages;
  return (
    <Fragment>
      {banner}
      {/* Page validations */}
      {hasValidationMessages ? (
        <ValidationsBar validations={errors.messages} />
      ) : null}
      <div className="bb hairline-1">
        <EditorToolbar
          customClasses={{
            container: "flex flex-row justify-end w-100 bg-white pt2 pb2"
          }}
        />
      </div>
      <div
        ref={ref}
        id={EDITOR_PREVIEW_ID}
        className="w-100 h-100 bg-content--secondary relative overflow-y-scroll"
      >
        <div
          className="w-100 center pt3 bg-content--secondary relative ph3"
          style={{
            maxWidth:
              editorViewport === VIEWPORT_SIZE.MOBILE
                ? VIEWPORT_MOBILE_WIDTH
                : FORMER_MAX_WIDTH
          }}
        >
          <PagePreviewBrowserBezel liveUrl={liveManifestUrl} />
          <div className="bl br bb hairline-1 preview-styles BLOCKS__layout overflow-hidden">
            <PageFactory {...values.manifest} />
            {previewStyles}
            {blocksStyles}
          </div>
          <Padder customClasses="dib h5-5 w-100 bg-transparent" />
        </div>
      </div>
    </Fragment>
  );
};

/**
 * PageFactory inputs
 * - entities
 * - content
 * - TODO: write function to strip out non essentials
 */

const Preview = () => {
  const { values } = useFormikContext();
  useRecomputeManifest();
  const entities = get(values, STATE_KEYS.MANIFEST.ENTITIES, []);

  return entities.length > 0 ? (
    <PageFactoryContextProvider
      {...PREVIEW_PAGE_FACTORY_SETTINGS}
      admin={get(values, STATE_KEYS.MANIFEST.ADMIN)}
    >
      <PageFactoryInContext />
    </PageFactoryContextProvider>
  ) : (
    <div className="ph3">
      <PreviewLoader withShimmer={false} />
    </div>
  );
};

export default Preview;
